import * as THREE from "three";
import { Card } from "./Card";
import { CARD, buildCardTextures } from "./content";
import { loadFont, loadImageOptional } from "./loader";
import { Polaroid } from "./Polaroid";

// ---------------------------------------------------------------------------
// Renderer / scene setup
// ---------------------------------------------------------------------------

const canvas = document.getElementById("scene") as HTMLCanvasElement;
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;
// Shadows turned off: the cast-shadow plane was producing a hard trapezoid
// underneath the card from the directional light's shadow frustum boundary.
// The card looks plenty dimensional from lighting + the bg vignette alone.
renderer.shadowMap.enabled = false;

const scene = new THREE.Scene();

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
camera.position.set(0, -0.4, 4.4);
camera.lookAt(0, 0, 0);

// ---------------------------------------------------------------------------
// Lighting
// ---------------------------------------------------------------------------

scene.add(new THREE.AmbientLight(0xfff1d9, 0.55));

const key = new THREE.DirectionalLight(0xfff0d4, 1.4);
key.position.set(2.5, 4, 4);
scene.add(key);

const rim = new THREE.DirectionalLight(0x8aa4ff, 0.35);
rim.position.set(-3, 2, -2);
scene.add(rim);

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

const PAGE_W = 1.4;
const PAGE_H = 1.9;

// ---------------------------------------------------------------------------
// Async asset bootstrap: load the hand-written font and the user's photos
// before composing the canvas textures. Everything else (renderer, lights,
// resize listener) is already attached so the page paints a black scene
// almost immediately while these resolve.
// ---------------------------------------------------------------------------

// Cover photo gets baked into the cover texture; the rest become draggable
// 3D Polaroid meshes on the inside-left page.
const COVER_URL = "/photos/FullSizeRender.jpeg";
const INSIDE_PHOTO_URLS = [
  "/photos/IMG_5509.jpeg",
  "/photos/PXL_20230802_001815950.jpg",
];

const [coverPhoto, insidePhotos] = await Promise.all([
  loadImageOptional(COVER_URL),
  Promise.all(INSIDE_PHOTO_URLS.map((u) => loadImageOptional(u))),
  loadFont("AnishHand", "/fonts/AnishHand-Regular.ttf"),
]);

const card = new Card({
  width: PAGE_W,
  height: PAGE_H,
  textures: buildCardTextures({ coverPhoto }),
});

// Card sits inside cardRoot. The card's local frame has its spine at x=0 and
// content extending into +x; when closed we shift cardRoot left by W/2 so the
// closed cover reads centered on screen, and animate it back to 0 as the card
// opens so the open spread (x:[-W..+W]) is recentered. updateLayout() owns
// both this offset and the camera distance.
const cardRoot = new THREE.Group();
cardRoot.add(card.group);
cardRoot.rotation.x = -0.18;
scene.add(cardRoot);

// Tracks normalized openness 0..1. Updated by Card during the flip and used
// by updateLayout() so resize, idle frames, and the flip all read from the
// same source of truth.
let openness = 0;

/**
 * Compute the camera distance required to fit a target rectangle (width x
 * height in world units) inside the current viewport, given a vertical FOV.
 * Picks the larger of the two axis-fit distances so nothing is clipped.
 */
function fitDistance(targetW: number, targetH: number, aspect: number, fovYDeg: number): number {
  const fovY = THREE.MathUtils.degToRad(fovYDeg);
  const distH = targetH / 2 / Math.tan(fovY / 2);
  const distW = targetW / 2 / Math.tan(fovY / 2) / aspect;
  return Math.max(distH, distW);
}

/**
 * Recompute camera position and card root x based on the current openness
 * (0..1) and viewport aspect ratio. Called from the flip onUpdate, window
 * resize, and once at startup.
 */
function updateLayout() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const aspect = w / h;
  const isPortrait = aspect < 1;

  // Closed: fit a single page (W x H) with margin.
  // Open:   fit the two-page spread (2W x H) with margin.
  // The forward tilt (rotation.x = -0.18) effectively makes the page taller
  // in screen space, so we pad the height target a bit. Portrait viewports
  // (phones) get aggressive margins — < 1.0 on the open width means we let
  // the spread overflow horizontally so the handwritten message reads big.
  const closedPadW = isPortrait ? 1.05 : 1.45;
  const closedPadH = isPortrait ? 1.05 : 1.35;
  const openPadW = isPortrait ? 0.88 : 1.18;
  const openPadH = isPortrait ? 1.0 : 1.35;
  const closedZ = fitDistance(PAGE_W * closedPadW, PAGE_H * closedPadH, aspect, 35);
  const openZ = fitDistance(PAGE_W * 2 * openPadW, PAGE_H * openPadH, aspect, 35);
  camera.position.z = THREE.MathUtils.lerp(closedZ, openZ, openness);

  // Vertical position: the forward tilt (rotation.x = -0.18) shifts the card's
  // visual centroid below world origin, so we keep camera y above origin to
  // pull the rendered card back up. Portrait viewports also reserve a little
  // room at the bottom for the OPEN/CLOSE button.
  camera.position.y = isPortrait ? 0.25 : 0.0;

  // Recenter card: closed -> cover centered on screen (cardRoot.x = -W/2).
  //                open   -> spread centered on screen (cardRoot.x = 0).
  // Portrait open shifts a touch left so the message page (right) is more
  // central — the polaroids on the left page partially overhang screen but
  // are still draggable into view.
  const openX = isPortrait ? -PAGE_W * 0.12 : 0;
  cardRoot.position.x = THREE.MathUtils.lerp(-PAGE_W / 2, openX, openness);
  // Lift the card slightly so it reads centered against the bottom UI.
  cardRoot.position.y = isPortrait ? 0.1 : 0.0;

  camera.aspect = aspect;
  camera.lookAt(0, isPortrait ? 0.05 : -0.05, 0);
  camera.updateProjectionMatrix();
}

card.onFlipProgress = (o) => {
  openness = o;
  updateLayout();
  syncPolaroidsVisibility();
};

// ---------------------------------------------------------------------------
// Draggable 3D polaroids on the inside-left page.
//
// Each polaroid is a textured plane placed in cardRoot's local frame so it
// inherits the card's tilt and idle sway -- they look glued to the page.
// They start at small offsets on the left half of the spread (x in [-W..0])
// and are hidden when the card is closed.
// ---------------------------------------------------------------------------

const polaroids: Polaroid[] = [];
const initialPolaroidPlacements = [
  { x: -PAGE_W * 0.65, y: 0.25, z: 0.012, rot: -0.18 },
  { x: -PAGE_W * 0.35, y: -0.25, z: 0.011, rot: 0.12 },
];

for (let i = 0; i < insidePhotos.length; i++) {
  const img = insidePhotos[i];
  if (!img) continue;
  const place = initialPolaroidPlacements[i] ?? initialPolaroidPlacements[0];
  const p = new Polaroid({ photo: img, width: 0.6 });
  p.mesh.position.set(place.x, place.y, place.z);
  p.mesh.rotation.z = place.rot;
  p.mesh.visible = false; // hidden until the card is open
  cardRoot.add(p.mesh);
  polaroids.push(p);
}

function syncPolaroidsVisibility() {
  // Reveal once the card is more than ~70% open so they don't pop in mid-flip.
  const visible = openness > 0.7;
  for (const p of polaroids) p.mesh.visible = visible;
}

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------

const toggleBtn = document.getElementById("toggle") as HTMLButtonElement;
const hintEl = document.getElementById("hint") as HTMLDivElement;
const captionEl = document.getElementById("caption") as HTMLDivElement;

function refreshUI() {
  toggleBtn.textContent = card.isOpen ? "Close" : "Open";
}

card.onToggle = () => refreshUI();
refreshUI();

async function toggle() {
  if (card.isAnimating) return;
  hideHint();
  await card.toggle();
}

toggleBtn.addEventListener("click", toggle);

window.addEventListener("keydown", (e) => {
  if (e.key === " " || e.key === "Enter" || e.key === "ArrowRight" || e.key === "ArrowLeft") {
    e.preventDefault();
    toggle();
  }
});

// ---------------------------------------------------------------------------
// Pointer interactions: polaroid drag takes priority; if the press doesn't
// hit a polaroid, we fall back to the existing tap-to-toggle / swipe-to-toggle
// behavior.
// ---------------------------------------------------------------------------

const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();
const dragPlane = new THREE.Plane();
const dragHit = new THREE.Vector3();

let draggingPolaroid: Polaroid | null = null;
let dragOffsetLocal: THREE.Vector3 | null = null;
let dragOriginalZ = 0;

let pressInfo: { x: number; y: number; t: number; pid: number } | null = null;

function setNdc(e: PointerEvent) {
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
}

function tryStartPolaroidDrag(e: PointerEvent): boolean {
  if (!card.isOpen || card.isAnimating) return false;
  if (polaroids.length === 0) return false;
  setNdc(e);
  raycaster.setFromCamera(ndc, camera);
  const meshes = polaroids.filter((p) => p.mesh.visible).map((p) => p.mesh);
  const hits = raycaster.intersectObjects(meshes, false);
  if (hits.length === 0) return false;
  const hit = hits[0];
  const polaroid = hit.object.userData.polaroid as Polaroid | undefined;
  if (!polaroid) return false;

  // Build a drag plane coplanar with the polaroid in world space, so as the
  // pointer moves we can intersect it to get a stable target point.
  const normalWorld = new THREE.Vector3(0, 0, 1).applyQuaternion(
    polaroid.mesh.getWorldQuaternion(new THREE.Quaternion()),
  );
  dragPlane.setFromNormalAndCoplanarPoint(
    normalWorld,
    polaroid.mesh.getWorldPosition(new THREE.Vector3()),
  );

  // Convert the hit point into cardRoot local space and remember the offset
  // from the polaroid's center so the drag feels grabbed-from-where-you-tapped.
  const hitLocal = cardRoot.worldToLocal(hit.point.clone());
  dragOffsetLocal = hitLocal.sub(polaroid.mesh.position);
  draggingPolaroid = polaroid;
  dragOriginalZ = polaroid.mesh.position.z;
  // Lift the polaroid slightly while held + bring it to the top of the stack.
  polaroid.mesh.position.z = dragOriginalZ + 0.03;
  cardRoot.remove(polaroid.mesh);
  cardRoot.add(polaroid.mesh); // re-add at end of children for top draw
  canvas.setPointerCapture(e.pointerId);
  return true;
}

function updatePolaroidDrag(e: PointerEvent) {
  if (!draggingPolaroid || !dragOffsetLocal) return;
  setNdc(e);
  raycaster.setFromCamera(ndc, camera);
  if (!raycaster.ray.intersectPlane(dragPlane, dragHit)) return;
  const local = cardRoot.worldToLocal(dragHit.clone());
  // Constrain to the inside-left page bounds (x in [-W..0], y in [-H/2..H/2]).
  const halfH = PAGE_H / 2;
  const margin = 0.12; // let the polaroid hang slightly off the page edge
  const x = THREE.MathUtils.clamp(
    local.x - dragOffsetLocal.x,
    -PAGE_W - margin,
    margin,
  );
  const y = THREE.MathUtils.clamp(
    local.y - dragOffsetLocal.y,
    -halfH - margin,
    halfH + margin,
  );
  draggingPolaroid.mesh.position.x = x;
  draggingPolaroid.mesh.position.y = y;
}

function endPolaroidDrag(e: PointerEvent) {
  if (!draggingPolaroid) return;
  draggingPolaroid.mesh.position.z = dragOriginalZ;
  draggingPolaroid = null;
  dragOffsetLocal = null;
  if (canvas.hasPointerCapture(e.pointerId)) {
    canvas.releasePointerCapture(e.pointerId);
  }
}

canvas.addEventListener("pointerdown", (e) => {
  if (tryStartPolaroidDrag(e)) return;
  // Otherwise track for tap/swipe toggle.
  pressInfo = { x: e.clientX, y: e.clientY, t: performance.now(), pid: e.pointerId };
});
canvas.addEventListener("pointermove", (e) => {
  updatePolaroidDrag(e);
});
canvas.addEventListener("pointerup", (e) => {
  if (draggingPolaroid) {
    endPolaroidDrag(e);
    pressInfo = null;
    return;
  }
  if (!pressInfo || pressInfo.pid !== e.pointerId) return;
  const dx = e.clientX - pressInfo.x;
  const dy = e.clientY - pressInfo.y;
  const dt = performance.now() - pressInfo.t;
  const moved = Math.hypot(dx, dy);
  if (dt < 600 && moved < 12) {
    toggle();
  } else if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
    toggle();
  }
  pressInfo = null;
});
canvas.addEventListener("pointercancel", (e) => {
  endPolaroidDrag(e);
  pressInfo = null;
});

// Fade out the initial hint after the first interaction; never bring it back.
let hintHidden = false;
function hideHint() {
  if (hintHidden) return;
  hintHidden = true;
  hintEl.style.transition = "opacity 600ms ease";
  hintEl.style.opacity = "0";
  // Fully remove from layout once faded so it can't catch taps later.
  setTimeout(() => {
    hintEl.style.display = "none";
  }, 700);
}

// also fade in a small caption while the card is open
card.onFlipStart = (targetOpen) => {
  if (targetOpen) {
    captionEl.textContent = CARD.outerSub;
    captionEl.classList.add("visible");
  } else {
    captionEl.classList.remove("visible");
  }
};

// ---------------------------------------------------------------------------
// Debug API for the snapshot harness — drive flip uniforms directly.
// ---------------------------------------------------------------------------

(window as any).__card = {
  setCoverFlip(angle: number) {
    card.cover.flip = angle;
  },
  open: () => card.open(),
  close: () => card.close(),
  isOpen: () => card.isOpen,
};

// ---------------------------------------------------------------------------
// Resize + render loop
// ---------------------------------------------------------------------------

function resize() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  renderer.setSize(w, h, false);
  updateLayout();
}
window.addEventListener("resize", resize);
resize();

const clock = new THREE.Clock();
function tick() {
  const t = clock.getElapsedTime();
  // Gentle idle sway so the card feels alive. We freeze it while a polaroid
  // is being dragged so the drag plane (computed in world space) stays
  // coherent with the polaroid's parent transform.
  if (!draggingPolaroid) {
    cardRoot.rotation.y = Math.sin(t * 0.4) * 0.04;
  }
  renderer.render(scene, camera);
  requestAnimationFrame(tick);
}
tick();
